// The register = Jira issues ∪ brain actions (D1-a §1.3).
//
// Nothing on the standup screen may be a typed sentence, so every row here is a
// real object: a Jira issue, a brain action-register row, or the two merged.
// The merge rules, in full:
//
//   Jira issue, no action        → a row.
//   Action carrying jiraIssueKey → MERGED INTO that Jira row, never a second
//                                  row. The action supplies context/clientStatus;
//                                  the issue supplies status/assignee/priority.
//   Action with no jiraIssueKey  → a row with a `—` ref and status `Not raised`.
//
// Bands are the scope name for Jira rows and `action.workStream` for brain-only
// rows — an issue claimed by two scopes is ONE row carrying both names, banded
// under the first.
//
// Pure functions only: no React, no fetching, so all of this is unit-testable.

import type { ProjectAction, ProjectJiraIssueRow } from "../lib/brain-api";
import type { PillTone } from "./StatusPill";
import { deriveActionStatus } from "./actionRegisterStatus";

export const UNGROUPED_BAND = "(ungrouped)";
export const NOT_RAISED = "Not raised";

/** Jira's literal Backlog status — the one status hidden by default. */
const BACKLOG = "backlog";

export type RegisterRowKind = "jira" | "action" | "merged";

export interface RegisterRow {
  /** Stable react key: the Jira key when there is one, else the action id. */
  id: string;
  kind: RegisterRowKind;
  /** null renders as `—`. */
  issueKey: string | null;
  url: string | null;
  summary: string;
  /** What the status column shows: live Jira status, else the action's. */
  status: { label: string; tone: PillTone };
  /** The raw live Jira status name, when the row has one. */
  jiraStatus: string | null;
  statusCategory: string | null;
  /**
   * Normalised P1 | P2 | P3. Drives the left spine and brain-only rows; for a
   * Jira-backed row prefer `jiraPriority`, which is what Jira actually says.
   */
  priority: string;
  /**
   * Jira's own priority NAME ("Highest" … "Low"), null for brain-only rows.
   * Kept raw so the register shows what the board shows — squashing it into
   * P1/P2/P3 was inventing a scale Jira does not have (Isaac 2026-08-13).
   */
  jiraPriority: string | null;
  assignee: string | null;
  /** Jira account id when the row came from Jira — the only EXACT owner match. */
  assigneeAccountId: string | null;
  /** Every scope/workstream name that claims this row. */
  workStreams: string[];
  /** The band this row is grouped under. */
  band: string;
  flagged: boolean;
  done: boolean;
  /** Jira status is exactly `Backlog` — hidden until the reveal is clicked. */
  backlog: boolean;
  updated: string | null;
  /** Brain actions attached to this row (context + clientStatus). */
  actions: ProjectAction[];
  /**
   * Project this row belongs to — required for Jira write-through (transitions).
   * Set from the card/register context; actions may also carry their own.
   */
  projectId: string | null;
  /** JSM Organization / Account — Support desk customer; null when unset. */
  organization: string | null;
}

/** Coarse buckets keep reserved keys; swimlane chips use `status:` + label. */
export const STATUS_FILTER_PREFIX = "status:";

export type RegisterSortKey = "priority" | "status" | "organization";
export type RegisterSortDir = "asc" | "desc";

export function statusFilterKey(label: string): string {
  return `${STATUS_FILTER_PREFIX}${label}`;
}

export function statusFilterLabel(key: string): string | null {
  return key.startsWith(STATUS_FILTER_PREFIX) ? key.slice(STATUS_FILTER_PREFIX.length) : null;
}

/** Distinct open STATUS labels, ordered new → indeterminate → other, then A–Z. */
export function openStatusLabels(rows: RegisterRow[]): string[] {
  const byLabel = new Map<string, string>();
  for (const r of rows) {
    if (r.done) continue;
    const label = (r.status.label ?? "").trim();
    if (!label || label === "—") continue;
    if (!byLabel.has(label)) byLabel.set(label, (r.statusCategory ?? "").toLowerCase());
  }
  const catRank = (cat: string) => (cat === "new" ? 0 : cat === "indeterminate" ? 1 : 2);
  return [...byLabel.entries()]
    .sort((a, b) => {
      const d = catRank(a[1]) - catRank(b[1]);
      return d !== 0 ? d : a[0].localeCompare(b[0]);
    })
    .map(([label]) => label);
}

/** Open / Done / All / status:<label> — matches the person-card Segmented control. */
export function matchesRegisterFilter(row: RegisterRow, filter: string): boolean {
  const statusLabel = statusFilterLabel(filter);
  if (statusLabel !== null) return !row.done && row.status.label === statusLabel;
  if (filter === "open") return !row.done;
  if (filter === "done") return row.done;
  return true; // "all" or unknown → keep
}

/**
 * OR across a multi-select selection — a row survives if it matches ANY chosen
 * filter. Statuses are mutually exclusive per row, so ANDing them would always
 * empty the table; OR is the only reading that makes "In Progress + Planning"
 * mean what the room means by it. An empty selection is "no filter".
 */
export function matchesRegisterFilters(row: RegisterRow, filters: string[]): boolean {
  if (!filters.length) return true;
  return filters.some((f) => matchesRegisterFilter(row, f));
}

/**
 * One ordering across both scales, so a register mixing Jira issues and brain
 * actions still sorts sensibly: Jira's five levels interleave with P1/P2/P3.
 */
const PRIORITY_RANK: Record<string, number> = {
  highest: 1, blocker: 1, critical: 1, p1: 1,
  high: 2, major: 2, p2: 2,
  medium: 3, normal: 3, p3: 3,
  low: 4, minor: 4,
  lowest: 5, trivial: 5,
};

export function priorityRank(row: Pick<RegisterRow, "priority" | "jiraPriority">): number {
  const raw = (row.jiraPriority ?? row.priority ?? "").trim().toLowerCase();
  return PRIORITY_RANK[raw] ?? 3;
}

/** Stable sort within a band. Missing org sorts last in asc. */
export function sortRegisterRows(
  rows: RegisterRow[],
  key: RegisterSortKey,
  dir: RegisterSortDir,
): RegisterRow[] {
  const mul = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (key === "priority") {
      cmp = priorityRank(a) - priorityRank(b);
    } else if (key === "status") {
      cmp = a.status.label.localeCompare(b.status.label);
    } else {
      const ao = (a.organization ?? "").trim();
      const bo = (b.organization ?? "").trim();
      if (!ao && bo) cmp = 1;
      else if (ao && !bo) cmp = -1;
      else cmp = ao.localeCompare(bo);
    }
    if (cmp !== 0) return cmp * mul;
    return (a.issueKey ?? a.id).localeCompare(b.issueKey ?? b.id);
  });
}

/** Jira priority names → the P1/P2/P3 pill the table draws. */
export function normalisePriority(raw: string | null | undefined): string {
  const p = (raw ?? "").trim().toLowerCase();
  if (!p) return "P3";
  if (p === "p1" || p === "highest" || p === "blocker" || p === "critical") return "P1";
  if (p === "p2" || p === "high" || p === "major") return "P2";
  return "P3";
}

/** Tone for a live Jira status, given its category and blocked flag. */
export function jiraStatusTone(
  statusCategory: string | null | undefined,
  status: string | null | undefined,
  flagged: boolean,
): PillTone {
  const cat = (statusCategory ?? "").toLowerCase();
  if (cat === "done") return "success";
  if (flagged || /block/i.test(status ?? "")) return "danger";
  if (cat === "indeterminate") return "warning";
  return "neutral";
}

function isBacklog(status: string | null | undefined): boolean {
  return (status ?? "").trim().toLowerCase() === BACKLOG;
}

function rowFromIssue(issue: ProjectJiraIssueRow, projectId: string | null): RegisterRow {
  const workStreams = issue.workStreams?.length ? [...issue.workStreams] : [];
  const done = (issue.statusCategory ?? "").toLowerCase() === "done";
  return {
    id: issue.key,
    kind: "jira",
    issueKey: issue.key,
    url: issue.url || null,
    summary: issue.summary,
    status: {
      label: issue.status || "—",
      tone: jiraStatusTone(issue.statusCategory, issue.status, issue.flagged),
    },
    jiraStatus: issue.status || null,
    statusCategory: issue.statusCategory || null,
    priority: normalisePriority(issue.priority),
    jiraPriority: issue.priority?.trim() || null,
    assignee: issue.assignee,
    assigneeAccountId: issue.assigneeAccountId ?? null,
    workStreams,
    band: workStreams[0] ?? UNGROUPED_BAND,
    flagged: issue.flagged,
    done,
    backlog: isBacklog(issue.status),
    updated: issue.updated || null,
    actions: [],
    projectId,
    organization: issue.organization?.trim() || null,
  };
}

function rowFromAction(a: ProjectAction, projectId: string | null): RegisterRow {
  const st = deriveActionStatus(a);
  const key = a.jiraIssueKey?.trim().toUpperCase() || null;
  const band = a.workStream?.trim() || UNGROUPED_BAND;
  return {
    id: a.id,
    // An action naming an issue outside every scope is still action-shaped —
    // we have its ref but none of Jira's live fields.
    kind: "action",
    issueKey: key,
    url: null,
    summary: a.text,
    status: st,
    jiraStatus: null,
    statusCategory: null,
    priority: normalisePriority(a.priority),
    // A brain action has no Jira identity, so it has no Jira priority either.
    jiraPriority: null,
    assignee: a.assignee?.trim() || null,
    // A brain action names a person in free text; it has no Jira identity.
    assigneeAccountId: null,
    workStreams: band === UNGROUPED_BAND ? [] : [band],
    band,
    flagged: a.kind === "blocker" && a.status !== "done" && a.status !== "dismissed",
    // Dismissed is closed work — surfaces under Done / All, never Open.
    done: a.status === "done" || a.status === "dismissed",
    // A brain action has no Jira status, so it is never hidden as backlog.
    backlog: false,
    updated: a.updatedAt ? new Date(a.updatedAt).toISOString() : null,
    actions: [a],
    projectId: a.projectId?.trim() || projectId,
    organization: null,
  };
}

/**
 * Merge an action into the Jira row it names. The issue keeps status/assignee/
 * priority (it is the live truth); the action contributes its workstream and
 * its context/clientStatus, which the table renders alongside.
 */
function mergeActionInto(row: RegisterRow, a: ProjectAction): void {
  row.kind = "merged";
  row.actions.push(a);
  const ws = a.workStream?.trim();
  if (ws && !row.workStreams.includes(ws)) row.workStreams.push(ws);
  if (row.band === UNGROUPED_BAND && ws) row.band = ws;
  if (!row.assignee && a.assignee?.trim()) row.assignee = a.assignee.trim();
  // A row someone raised a brain action on is IN FLIGHT by definition, whatever
  // Jira's backlog column says. The backlog filter exists to hide untriaged
  // noise (Shelley's 98 open), not to swallow work a human is actively chasing.
  row.backlog = false;
}

/**
 * Build the register: Jira issues ∪ brain actions, deduped so one issue is one
 * row no matter how many scopes or actions point at it.
 */
export function buildRegisterRows(input: {
  issues?: ProjectJiraIssueRow[] | null;
  actions?: ProjectAction[] | null;
  /** Card/register context — stamped onto every row for Jira write-through. */
  projectId?: string | null;
}): RegisterRow[] {
  const projectId = input.projectId?.trim() || null;
  const rows: RegisterRow[] = [];
  const byKey = new Map<string, RegisterRow>();

  for (const issue of input.issues ?? []) {
    const key = (issue.key ?? "").trim().toUpperCase();
    if (!key) continue;
    // The server already dedupes across scopes; belt-and-braces for a stale or
    // hand-built payload so a repeat key can never become a second row.
    const hit = byKey.get(key);
    if (hit) {
      for (const ws of issue.workStreams ?? []) {
        if (!hit.workStreams.includes(ws)) hit.workStreams.push(ws);
      }
      continue;
    }
    const row = rowFromIssue(issue, projectId);
    byKey.set(key, row);
    rows.push(row);
  }

  for (const a of input.actions ?? []) {
    // Dismissed actions stay as their own closed rows (Done / All views). Do not
    // merge into a live Jira row — that would smear dismissed context onto open work.
    if (a.status === "dismissed") {
      rows.push(rowFromAction(a, projectId));
      continue;
    }
    const key = a.jiraIssueKey?.trim().toUpperCase();
    const target = key ? byKey.get(key) : undefined;
    if (target) {
      mergeActionInto(target, a);
      continue;
    }
    const row = rowFromAction(a, projectId);
    // Two actions naming the same out-of-scope issue are still one row.
    if (key) {
      const seen = byKey.get(key);
      if (seen) {
        mergeActionInto(seen, a);
        continue;
      }
      byKey.set(key, row);
    }
    rows.push(row);
  }

  return rows;
}

export interface RegisterCounts {
  /** Not backlog, not done — what the room actually talks about. */
  inFlight: number;
  backlog: number;
  done: number;
  doneThisWeek: number;
  /** Rows shown by default (everything but backlog). */
  visible: number;
  total: number;
  /** Ring %: done over the in-flight set, never inflated by backlog. */
  pct: number;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Counters and the ring are computed over the IN-FLIGHT set. Shelley has 98
 * open issues and 12 actually in flight; a card that counts all 98 is noise.
 */
export function registerCounts(rows: RegisterRow[], now: number = Date.now()): RegisterCounts {
  const visible = rows.filter((r) => !r.backlog);
  const done = visible.filter((r) => r.done);
  const doneThisWeek = done.filter((r) => {
    if (!r.updated) return false;
    const t = Date.parse(r.updated);
    return Number.isFinite(t) && now - t <= WEEK_MS;
  });
  return {
    inFlight: visible.length - done.length,
    backlog: rows.length - visible.length,
    done: done.length,
    doneThisWeek: doneThisWeek.length,
    visible: visible.length,
    total: rows.length,
    pct: visible.length ? Math.round((done.length / visible.length) * 100) : 0,
  };
}

/** `4 in flight · 12 backlog · 3 done this week` — omits the zeroes. */
export function registerMetaLine(counts: RegisterCounts): string {
  const parts = [`${counts.inFlight} in flight`];
  if (counts.backlog) parts.push(`${counts.backlog} backlog`);
  if (counts.doneThisWeek) parts.push(`${counts.doneThisWeek} done this week`);
  return parts.join(" · ");
}

/**
 * Band the rows. `order` is the project's scope names, so the bands read in the
 * order the project declares them; anything else follows, ungrouped last.
 */
export function groupRegisterRows(
  rows: RegisterRow[],
  order: string[] = [],
): { band: string; rows: RegisterRow[] }[] {
  const map = new Map<string, RegisterRow[]>();
  for (const r of rows) {
    const band = r.band || UNGROUPED_BAND;
    if (!map.has(band)) map.set(band, []);
    map.get(band)!.push(r);
  }
  const out: { band: string; rows: RegisterRow[] }[] = [];
  const seen = new Set<string>();
  for (const band of order) {
    if (map.has(band) && !seen.has(band)) {
      out.push({ band, rows: map.get(band)! });
      seen.add(band);
    }
  }
  for (const [band, list] of map) {
    if (band === UNGROUPED_BAND || seen.has(band)) continue;
    out.push({ band, rows: list });
  }
  if (map.has(UNGROUPED_BAND)) out.push({ band: UNGROUPED_BAND, rows: map.get(UNGROUPED_BAND)! });
  return out;
}
